// Core
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

// Hooks
import { UseOverflowTooltipOptions, OverflowTooltipHandlers } from './useOverflowTooltip.types';

export const useOverflowTooltip = ({
  widthOffset = 0,
  overflowCheckTriggers,
}: UseOverflowTooltipOptions): OverflowTooltipHandlers => {
  // Refs
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  // State
  const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(false);

  const checkOverflowVisibility = useCallback((): void => {
    const container: HTMLDivElement | null = containerRef.current;
    const content: HTMLDivElement | null = contentRef.current;

    if (container) {
      // If we have both container and content, use the original logic
      if (content) {
        const containerMaxWidth: number = container.scrollWidth;
        const contentWidth: number = content.scrollWidth;

        if (contentWidth >= containerMaxWidth - widthOffset) {
          setIsTooltipVisible(true);
        } else {
          setIsTooltipVisible(false);
        }
      } else {
        // If we only have container, check if the container itself has overflow
        const isOverflowing: boolean = container.scrollWidth > container.clientWidth;
        setIsTooltipVisible(isOverflowing);
      }
    } else {
      setIsTooltipVisible(false);
    }
  }, [widthOffset]);

  useEffect((): () => void => {
    const handleVisibilityCheck = (): void => {
      setTimeout(checkOverflowVisibility, 0);
    };

    handleVisibilityCheck();
    window.addEventListener('resize', handleVisibilityCheck);

    return () => {
      window.removeEventListener('resize', handleVisibilityCheck);
    };
  }, [checkOverflowVisibility, overflowCheckTriggers]);

  return {
    checkOverflowVisibility,
    containerRef,
    contentRef,
    isTooltipVisible,
  };
};
